import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ShieldCheck } from 'lucide-react';

export default function PrivacyPolicy() {
    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-8">
                    <Link to="/" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mb-8 transition-colors">
                        <ChevronLeft size={16} className="mr-1" />
                        Back to Login
                    </Link>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-green-50 rounded-lg">
                            <ShieldCheck className="text-green-600" size={24} />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 font-display">Privacy Policy</h1>
                    </div>

                    <p className="text-sm text-gray-500 mb-8 border-b pb-4">Last Updated: March 08, 2026</p>

                    <div className="prose prose-blue prose-sm max-w-none text-gray-600 space-y-6">
                        <section>
                            <h2 className="text-xl font-semibold text-gray-800 mb-3">1. Data Privacy Commitment</h2>
                            <p>At MindConnect, we understand that therapy data is highly sensitive. We are committed to maintaining the highest standards of data privacy and security to protect clinicians, supervisors, and patients.</p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-gray-800 mb-3">2. Information We Collect</h2>
                            <p className="mb-2">We process the following types of information:</p>
                            <ul className="list-disc pl-5 space-y-1">
                                <li><span className="font-medium">User Information:</span> Names, emails, and professional credentials for account management.</li>
                                <li><span className="font-medium">Transcription Data:</span> Audio/textual records of therapy sessions for clinical evaluation.</li>
                                <li><span className="font-medium">Evaluation Metadata:</span> Supervisor corrections, signal extracts, and performance metrics.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-gray-800 mb-3">3. How We Use Data</h2>
                            <p>Data is used exclusively for:</p>
                            <ul className="list-disc pl-5 space-y-1">
                                <li>Automating clinical signal extraction using AI.</li>
                                <li>Providing a workspace for expert supervisory review.</li>
                                <li>Enhancing the accuracy of our evaluations via privacy-preserving RAG techniques within organizational boundaries.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-gray-800 mb-3">4. Data Protection & Security</h2>
                            <p>We implement comprehensive technical measures to protect your data:</p>
                            <ul className="list-disc pl-5 space-y-1">
                                <li>End-to-end encryption for all session data.</li>
                                <li>Organizational isolation ensured through strict database filtering and multi-tenancy architecture.</li>
                                <li>Audit logs for all access potentially impacting sensitive data.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-gray-800 mb-3">5. Data Retention</h2>
                            <p>Session data and evaluations are retained only as long as required for the purpose of the clinician's training or as legally mandated by your institution. You can request deletion of your organization's data at any time.</p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-gray-800 mb-3">6. Third-Party Services</h2>
                            <p>We utilize trusted AI infrastructure providers for signal extraction. Data shared with these providers is strictly for processing and is governed by non-training agreements to ensure your sessions are never used to train global models.</p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold text-gray-800 mb-3">7. Contact Us</h2>
                            <p>If you have any questions about this Privacy Policy or our data practices, please contact your organization's administrator or our privacy team.</p>
                        </section>

                        <section className="pt-8 border-t">
                            <p className="text-xs text-center text-gray-400">© 2026 MindConnect by NeuralLoom Labs. Your privacy is our priority.</p>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}
